// ============================================================
// A CRIPPLED PART'S VHS SNOW (plan plate-unboxed; the user's pick, "like
// the end of a VHS recording"): one tile of grey-white noise - wide
// pixels at random levels - that the plate fills a crippled location's
// shape with, and jumps about in steps so it flickers in place. Seeded,
// so every plate draws the same tile and a test can hold it.
//
// Pure. Zero React dependency.
// ============================================================

import { mulberry32 } from '../mulberry32';

/** The tile's side, in frame units; the pattern repeats it. */
export const SNOW_TILE = 180;

/** One noise pixel: wide (9 x 6 units), as tape noise is. */
export const SNOW_CELL = { w: 9, h: 6 } as const;

/** A pixel of the tile: where it sits, and how bright it is (0.12 dark grey to 1 white). */
export interface SnowCell {
  x: number;
  y: number;
  level: number;
}

/** The darkest a pixel goes, as a share of white (the mock's 30 of 255). */
const FLOOR = 30 / 255;

/** The tile's pixels, row by row, at seeded levels between the floor and white (the engine's one generator). */
export function snowTile(seed = 11): SnowCell[] {
  const rnd = mulberry32(seed);
  const cells: SnowCell[] = [];
  for (let y = 0; y < SNOW_TILE; y += SNOW_CELL.h) {
    for (let x = 0; x < SNOW_TILE; x += SNOW_CELL.w) cells.push({ x, y, level: FLOOR + rnd() * (1 - FLOOR) });
  }
  return cells;
}

/** How many greys the image holds: few enough for one path each, too many apart for the eye to count. */
const GREYS = 15;

/**
 * The tile as one small SVG image (a data URI): its pixels' levels in sixteen greys, one path each, white on clear.
 * The plate uses it as a mask's light over a rect in the snow's own colour (a theme token cannot reach inside an
 * image), so a crippled part draws one image instead of six hundred rects.
 */
export function snowImage(seed = 11): string {
  const byGrey = new Map<number, string[]>();
  for (const c of snowTile(seed)) {
    const grey = Math.round(c.level * GREYS) / GREYS;
    const cells = byGrey.get(grey) ?? [];
    cells.push(`M${c.x} ${c.y}h${SNOW_CELL.w}v${SNOW_CELL.h}h-${SNOW_CELL.w}z`);
    byGrey.set(grey, cells);
  }
  const paths = [...byGrey]
    .map(([grey, cells]) => `<path fill="white" fill-opacity="${grey.toFixed(3)}" d="${cells.join('')}"/>`)
    .join('');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${SNOW_TILE}" height="${SNOW_TILE}">${paths}</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}
